import { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import WallFeed from "@/components/WallFeed";
import PhotosOfWeek from "@/components/PhotosOfWeek";
import LeaderboardCard from "@/components/LeaderboardCard";
import CalendarSyncCard from "@/components/CalendarSyncCard";
import { useI18n } from "@/i18n/i18n";
import PageHeader from "@/components/PageHeader";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Link } from "react-router-dom";
import { Users as UsersIcon } from "lucide-react";
import ClassCardActions from "@/components/ClassCardActions";

export default function TeacherHome() {
  const { t } = useI18n();
  const [groups, setGroups] = useState([]);
  const [sedes, setSedes] = useState([]);
  const [programs, setPrograms] = useState([]);
  const [enrollments, setEnrollments] = useState([]);

  useEffect(() => {
    (async () => {
      const [g, s, p, e] = await Promise.all([
        api.get("/groups"), api.get("/sedes"), api.get("/programs"), api.get("/enrollments"),
      ]);
      setGroups(g.data); setSedes(s.data); setPrograms(p.data); setEnrollments(e.data);
    })();
  }, []);

  const sedesMap = useMemo(() => Object.fromEntries(sedes.map((x) => [x.id, x.name])), [sedes]);
  const programsMap = useMemo(() => Object.fromEntries(programs.map((x) => [x.id, x.name])), [programs]);
  const countFor = (gid) => enrollments.filter((e) => e.group_id === gid).length;

  return (
    <div>
      <PageHeader title={t("my_groups")} subtitle={t("tagline")} testId="teacher-home-header" />
      {groups.length === 0 && (
        <div className="bg-white border border-slate-200 rounded-lg p-10 text-center text-slate-500">
          {t("no_data")}
        </div>
      )}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {groups.map((g) => (
          <Card key={g.id} className="p-5 hover:-translate-y-1 hover:shadow-md hover:border-slate-300 transition-all" data-testid={`teacher-group-${g.id}`}>
            <div className="flex items-center gap-2">
              <h3 className="font-display text-lg font-semibold">{g.name}</h3>
              {g.is_private && <Badge variant="secondary">{t("private")}</Badge>}
            </div>
            <p className="text-xs text-slate-500 mt-1">
              {sedesMap[g.sede_id]} · {programsMap[g.program_id]}
            </p>
            {g.schedule && <p className="text-xs text-slate-500">{t("schedule")}: {g.schedule}</p>}
            <div className="flex items-center gap-2 mt-3 text-xs text-slate-600">
              <UsersIcon className="h-3.5 w-3.5" /> {countFor(g.id)} {t("members").toLowerCase()}
            </div>
            <ClassCardActions group={g} />
          </Card>
        ))}
      </div>

      <div className="space-y-8 mt-10">
        <CalendarSyncCard compact variant="teacher" />
        <LeaderboardCard />
        <PhotosOfWeek />
        <WallFeed limit={1} allLink="/dashboard/wall-board" />
      </div>
    </div>
  );
}
